import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { generationService } from '@soundbyte/services';
import type { Generation, GenerationInput, ArtStyle, City, Resolution, ColorConfig } from '@soundbyte/types';
import { DEFAULT_ART_STYLE, DEFAULT_CITY, DEFAULT_COLOR_CONFIG, RESOLUTIONS } from '@soundbyte/config/constants';

// Journey types - determines the user flow and final product
export type JourneyType = 'originals' | 'sonic' | null;

interface GenerationState {
  // Generations list
  generations: Generation[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  page: number;

  // Selected journey type (originals or sonic)
  journeyType: JourneyType;

  // Current creation wizard state
  wizard: {
    step: number;
    voiceFile: File | null;
    textPrompt: string;
    artStyle: ArtStyle;
    city: City;
    resolution: Resolution;
    colorConfig: ColorConfig;
    isCreating: boolean;
  };

  // Actions - Journey
  setJourneyType: (type: JourneyType) => void;

  // Actions - List
  fetchGenerations: (reset?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  toggleFavorite: (id: string) => Promise<void>;
  togglePublic: (id: string) => Promise<void>;
  deleteGeneration: (id: string) => Promise<void>;

  // Clear all data (call on logout)
  clearAll: () => void;

  // Actions - Wizard
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setVoiceFile: (file: File | null) => void;
  setTextPrompt: (prompt: string) => void;
  setArtStyle: (style: ArtStyle) => void;
  setCity: (city: City) => void;
  setResolution: (resolution: Resolution) => void;
  setColorConfig: (config: Partial<ColorConfig>) => void;
  resetWizard: () => void;
  createGeneration: () => Promise<Generation | null>;
}

const initialWizardState = {
  step: 1,
  voiceFile: null,
  textPrompt: '',
  artStyle: DEFAULT_ART_STYLE,
  city: DEFAULT_CITY,
  resolution: RESOLUTIONS[0],
  colorConfig: DEFAULT_COLOR_CONFIG,
  isCreating: false,
};

export const useGenerationStore = create<GenerationState>()(
  persist(
    (set, get) => ({
      // Initial state
      generations: [],
      isLoading: false,
      error: null,
      hasMore: true,
      page: 1,
      journeyType: null,
      wizard: { ...initialWizardState },

      // Set journey type
      setJourneyType: (type) => set({ journeyType: type }),

  // Fetch generations
  fetchGenerations: async (reset = false) => {
    const { page } = get();
    const targetPage = reset ? 1 : page;

    set({ isLoading: true, error: null });

    const response = await generationService.getAll(targetPage, 12);

    if (response.success && response.data) {
      set({
        generations: reset
          ? response.data.data
          : [...get().generations, ...response.data.data],
        hasMore: targetPage < response.data.totalPages,
        page: targetPage,
        isLoading: false,
      });
    } else {
      set({
        error: response.error?.message || 'Failed to fetch generations',
        isLoading: false,
      });
    }
  },

  // Load more generations
  loadMore: async () => {
    const { hasMore, isLoading, page } = get();
    if (!hasMore || isLoading) return;

    set({ page: page + 1 });
    await get().fetchGenerations();
  },

  // Toggle favorite
  toggleFavorite: async (id) => {
    const response = await generationService.toggleFavorite(id);

    if (response.success && response.data) {
      set({
        generations: get().generations.map((g) =>
          g.id === id ? { ...g, isFavorite: response.data!.isFavorite } : g
        ),
      });
    } else {
      throw new Error(response.error?.message || 'Failed to update favorite');
    }
  },

  // Toggle public
  togglePublic: async (id) => {
    const response = await generationService.togglePublic(id);

    if (response.success && response.data) {
      set({
        generations: get().generations.map((g) =>
          g.id === id ? { ...g, isPublic: response.data!.isPublic } : g
        ),
      });
    } else {
      throw new Error(response.error?.message || 'Failed to update visibility');
    }
  },

  // Delete generation
  deleteGeneration: async (id) => {
    const response = await generationService.delete(id);

    if (response.success) {
      set({
        generations: get().generations.filter((g) => g.id !== id),
      });
    } else {
      throw new Error(response.error?.message || 'Failed to delete generation');
    }
  },

  // Clear all data (call on logout to prevent data leakage between accounts)
  clearAll: () => {
    set({
      generations: [],
      isLoading: false,
      error: null,
      hasMore: true,
      page: 1,
      journeyType: null,
      wizard: { ...initialWizardState },
    });
  },

  // Wizard actions
  setStep: (step) => set({ wizard: { ...get().wizard, step } }),

  nextStep: () => {
    const { step } = get().wizard;
    if (step < 4) {
      set({ wizard: { ...get().wizard, step: step + 1 } });
    }
  },

  prevStep: () => {
    const { step } = get().wizard;
    if (step > 1) {
      set({ wizard: { ...get().wizard, step: step - 1 } });
    }
  },

  setVoiceFile: (file) =>
    set({ wizard: { ...get().wizard, voiceFile: file } }),

  setTextPrompt: (prompt) =>
    set({ wizard: { ...get().wizard, textPrompt: prompt } }),

  setArtStyle: (style) =>
    set({ wizard: { ...get().wizard, artStyle: style } }),

  setCity: (city) =>
    set({ wizard: { ...get().wizard, city: city } }),

  setResolution: (resolution) =>
    set({ wizard: { ...get().wizard, resolution: resolution } }),

  setColorConfig: (config) =>
    set({
      wizard: {
        ...get().wizard,
        colorConfig: { ...get().wizard.colorConfig, ...config },
      },
    }),

  resetWizard: () =>
    set({ wizard: { ...initialWizardState } }),

  // Create generation
  createGeneration: async () => {
    const { wizard } = get();

    set({ wizard: { ...wizard, isCreating: true } });

    const input: GenerationInput = {
      voiceFile: wizard.voiceFile || undefined,
      textPrompt: wizard.textPrompt,
      artStyle: wizard.artStyle,
      city: wizard.city,
      resolution: wizard.resolution,
      colorConfig: wizard.colorConfig,
    };

    const response = await generationService.create(input);

    set({ wizard: { ...get().wizard, isCreating: false } });

    if (response.success && response.data) {
      // Add to beginning of generations list
      set({
        generations: [response.data, ...get().generations],
      });
      return response.data;
    }

    return null;
  },
    }),
    {
      name: 'soundbyte-generation',
      partialize: (state) => ({
        journeyType: state.journeyType,
      }),
    }
  )
);

// Selector hooks
export const useWizard = () => useGenerationStore((state) => state.wizard);
export const useGenerations = () => useGenerationStore((state) => state.generations);
export const useJourneyType = () => useGenerationStore((state) => state.journeyType);
